// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import { AddManualTimeRequest, AddManualTimeResponse, } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { setDialogStatus } from "../slice"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type AddManualTimeRpc = (request: AddManualTimeRequest) => Observable<AddManualTimeResponse>

export const addManualTime = (addManualTimeRpc: AddManualTimeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.addManualTime.match),
      mergeMap(action => of(action.payload).pipe(
        mergeMap(addManualTimeRpc),
        mergeMap(() => of(Actions.addManualTimeFulfilled())),
        catchError(handleError),
      ))
    )

const addManualTimeRpc = (request: AddManualTimeRequest): Observable<AddManualTimeResponse> =>
  grpc(() => timelineClient.addManualTime(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_manual_time_not_found", message: "Failed to add time because of an invalid time activity." }),
          messageType: "ERROR",
        })),
        of(setDialogStatus("CLOSED")),
      )
    }
  }

  return concat(
    of(showSnackBar({
      message: t({ id: "msg_add_manual_time_error", message: "Failed to add time. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    })),
    of(setDialogStatus("ERROR")),
  )
}

export default addManualTime(addManualTimeRpc)
